import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider } from "@/context/I18nContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import CommandCenter from "@/pages/CommandCenter";
import {
  CollectionsPage,
  StylesPage,
  MaterialsPage,
  FabricRollsPage,
  SuppliersPage,
  InventoryPage,
  IntegrationsPage,
  AdminPage,
  BOMPage,
  BOMDetailPage,
  ComingSoon,
} from "@/pages/SimplePages";
import {
  CustomersPage,
  AllOrdersPage,
  ShowroomOrdersPage,
  MadeToOrderPage,
  OrderDetailPage,
  ReservationsPage,
  ProductionPlanningPage,
  ProductionOrdersPage,
  ProductionOrderDetailPage,
  WIPBoardPage,
  MaterialRequirementsPage,
  QCPage,
  OrdersDashboardPage,
  ProductionDashboardPage,
} from "@/pages/Phase2Pages";
import FabricLibraryPage from "@/pages/FabricLibraryPage";
import BarcodesPage from "@/pages/BarcodesPage";
import { FinanceDashboard, ExpensesPage, PayablesPage } from "@/pages/FinancePages";
import {
  POSPage,
  AllSalesPage,
  ShowroomSalesPage,
  SaleDetailPage,
  ReturnsPage,
  SalesDashboardPage,
} from "@/pages/SalesPages";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
        <div className="font-serif text-2xl tracking-[0.3em] text-[#8F8D84]">NYLSU</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route index element={<CommandCenter />} />
              <Route path="products" element={<StylesPage />} />
              <Route path="products/barcodes" element={<BarcodesPage />} />
              <Route path="barcodes" element={<BarcodesPage />} />
              <Route path="finance" element={<FinanceDashboard />} />
              <Route path="finance/expenses" element={<ExpensesPage />} />
              <Route path="finance/payables" element={<PayablesPage />} />
              <Route path="products/collections" element={<CollectionsPage />} />
              <Route path="products/bom" element={<BOMPage />} />
              <Route path="bom" element={<BOMPage />} />
              <Route path="bom/:styleId" element={<BOMDetailPage />} />
              <Route path="collections" element={<CollectionsPage />} />
              <Route path="materials" element={<MaterialsPage />} />
              <Route path="materials/rolls" element={<FabricRollsPage />} />
              <Route path="materials/library" element={<FabricLibraryPage />} />
              <Route path="fabric-library" element={<FabricLibraryPage />} />
              <Route path="fabric-rolls" element={<FabricRollsPage />} />
              <Route path="suppliers" element={<SuppliersPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="admin" element={<AdminPage />} />

              {/* Phase 2 — Orders */}
              <Route path="orders" element={<AllOrdersPage />} />
              <Route path="orders/dashboard" element={<OrdersDashboardPage />} />
              <Route path="orders/showroom" element={<ShowroomOrdersPage />} />
              <Route path="orders/mto" element={<MadeToOrderPage />} />
              <Route path="orders/reservations" element={<ReservationsPage />} />
              <Route path="orders/:orderId" element={<OrderDetailPage />} />

              {/* Phase 2 — Production */}
              <Route path="production" element={<Navigate to="/production/dashboard" replace />} />
              <Route path="production/dashboard" element={<ProductionDashboardPage />} />
              <Route path="production/planning" element={<ProductionPlanningPage />} />
              <Route path="production/orders" element={<ProductionOrdersPage />} />
              <Route path="production/orders/:poId" element={<ProductionOrderDetailPage />} />
              <Route path="production/wip" element={<WIPBoardPage />} />
              <Route path="production/materials" element={<MaterialRequirementsPage />} />
              <Route path="production/qc" element={<QCPage />} />

              {/* Phase 2 — Customers */}
              <Route path="customers" element={<CustomersPage />} />

              {/* Phase 2.5 — Sales / POS */}
              <Route path="sales" element={<AllSalesPage />} />
              <Route path="sales/new" element={<POSPage />} />
              <Route path="sales/dashboard" element={<SalesDashboardPage />} />
              <Route path="sales/showroom" element={<ShowroomSalesPage />} />
              <Route path="sales/returns" element={<ReturnsPage />} />
              <Route path="sales/:saleId" element={<SaleDetailPage />} />

              <Route path="purchasing" element={<ComingSoon eyebrowKey="nav.purchasing" titleKey="coming.purchasing.title" subtitleKey="coming.purchasing.sub" />} />
              <Route path="reports" element={<ComingSoon eyebrowKey="nav.reports" titleKey="coming.reports.title" subtitleKey="coming.reports.sub" />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "var(--nylsu-card)",
              border: "1px solid var(--nylsu-border)",
              borderRadius: 0,
              fontFamily: "Manrope, sans-serif",
            },
          }}
        />
      </div>
    </AuthProvider>
    </I18nProvider>
  );
}
